import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { BuilderComponent, useIsPreviewing } from '@builder.io/react';
import { useCmsContentQuery } from '@/api/cms/cms';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { usePageMetaData } from '@/helpers';
import styles from './500.module.scss';
import Head from './_head.partial';
import Page from './_page.partial';


interface InternalServerErrorProps {
	error?: string;
	description?: string;
}

export const InternalServerError: React.FunctionComponent<InternalServerErrorProps> = ({ error, description }) => {
	const location = useLocation();
	const isPreviewingInBuilder = useIsPreviewing();
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: settings, isError: isSettingsError } = useGetSettings(segmentationId);
	const modelName = settings?.data.cmsSettings.internalServerError || '';

	const {
		data: content,
		isLoading,
		isError,
	} = useCmsContentQuery({
		url: location.pathname,
		settings: settings?.data,
		modelName,
	});
	const metaData = usePageMetaData(content, settings?.data, undefined, { skipCanonicalFallback: true });

	const errorMesssage = translations?.data.shared.genericErrorMsg ?? 'Sorry! Something went wrong...';

	if (process.env.NODE_ENV === 'development' && error) {
		return (
			<div className={styles.developerError}>
				{error && <h1 dangerouslySetInnerHTML={{ __html: error }} />}
				{description && <div dangerouslySetInnerHTML={{ __html: description.replaceAll('\n', '<br>') }} />}
			</div>
		);
	}

	if (isLoading) {
		return (
			<Page
				largeContentSpacer
				hasStickyHeader={!settings?.data.isSalesPortal}
				hasClassicHeader={!settings?.data.isSalesPortal}
			/>
		);
	}

	if (isSettingsError || isError || (!content && !isPreviewingInBuilder)) {
		return (
			<Page
				largeContentSpacer
				hasStickyHeader={!settings?.data.isSalesPortal}
				hasClassicHeader={!settings?.data.isSalesPortal}
			>
				<h1 className={styles.userError}>{errorMesssage}</h1>
			</Page>
		);
	}

	return (
		<Page
			largeContentSpacer
			hasStickyHeader={!settings?.data.isSalesPortal}
			hasClassicHeader={!settings?.data.isSalesPortal}
		>
			{metaData && <Head metaData={metaData} />}
			<BuilderComponent model={modelName} content={content || undefined} />
		</Page>
	);
};
